import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useGetRegion from './queries/useGetRegion';
import useUpdateRegion from './queries/useUpdateRegion';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import {
    breadCrumbTrailEdit as breadCrumbTrail,
    editHeader as header,
    regionsAndLocationsPath,
} from './constants';
import RegionNameField from './formFields/RegionNameField';
import VoicePoliciesMultiSelect from './formFields/VoicePoliciesMultiSelect';
import RegionLocationsOrderedList from './formFields/RegionLocationsOrderedList';

const RegionEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);

    const handleBack = () => {
        dispatch({
            type: 'CHANGE_TAB_VIEW',
            payload: { level1: 0, level2: 0 },
        });
        navigate(regionsAndLocationsPath);
    };

    const {
        data: regionData,
        isLoading: regionLoading,
        isFetching: regionFetching,
        isError: notFound,
    } = useGetRegion(id);

    const handleUpdateSuccess = () => {
        toast.success('Region updated successfully!');
        handleBack();
    };

    const handleUpdateError = (error) => {
        console.error('Failed to update region:', error);
        toast.error('Failed to update region. Please try again.');
    };

    const { mutate: updateRegion, isPending: updatePending } = useUpdateRegion(
        handleUpdateSuccess,
        handleUpdateError,
    );

    const finalFormSubmit = async (values) => {
        if (!values.name || values.name.length < 1) {
            console.error('Region name is required');
            return;
        }
        updateRegion({
            ...regionData,
            id,
            name: values.name,
            voicePolicies: values.voicePolicies,
            trunkLocations: values.trunkLocations,
        });
    };

    if (regionLoading || regionFetching) {
        return <LoadingIndicator />;
    }

    if (notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={{ name: '' }}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={handleBack}>
                {() => [
                    <p key="not-found">
                        This region no longer exists. It may have already been
                        deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    const editInitialValues = {
        name: regionData?.name || '',
        voicePolicies: regionData?.voicePolicies || [],
        trunkLocations: regionData?.trunkLocations || [],
    };

    const formFields = (errors, touched, values, setFieldValue) => [
        <RegionNameField
            key="name"
            errors={errors}
            touched={touched}
            darkMode={darkMode}
        />,
        <VoicePoliciesMultiSelect
            key="voicePolicies"
            darkMode={darkMode}
            values={values}
            setFieldValue={setFieldValue}
        />,
        <RegionLocationsOrderedList
            key="trunkLocations"
            darkMode={darkMode}
            regionId={id}
            values={values}
            setFieldValue={setFieldValue}
        />,
    ];

    return (
        <FormBase
            isSubmitDisabled={updatePending}
            isLoading={updatePending}
            breadCrumbTrail={breadCrumbTrail}
            header={header}
            initialValues={editInitialValues}
            finalFormSubmit={finalFormSubmit}
            backLink={handleBack}>
            {({ errors, touched, values, setFieldValue }) =>
                formFields(errors, touched, values, setFieldValue)
            }
        </FormBase>
    );
};

export default RegionEdit;
